import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  X,
  Image as ImageIcon,
  Loader2,
  Search,
  ChevronRight,
  Sparkles,
  Upload,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function useDropzone(onFile) {
  const [isOver, setIsOver] = useState(false);

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) onFile(file);
  };

  return { isOver, onDragOver, onDragLeave, onDrop };
}

export default function MoodCategoryManager() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const [formData, setFormData] = useState({ name: '', description: '' });

  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  const [saving, setSaving] = useState(false);

  const fileInputRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/mood-categories/list`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  // Lock body scroll when modal open
  useEffect(() => {
    if (!showModal) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showModal]);

  // ESC close modal
  useEffect(() => {
    if (!showModal) return;
    const onKeyDown = (ev) => {
      if (ev.key === 'Escape' && !saving) closeModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showModal, saving]);

  const filteredCategories = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((c) => {
      const name = (c.name || '').toLowerCase();
      const desc = (c.description || '').toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [categories, query]);

  const openModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({ name: category.name || '', description: category.description || '' });
      setCoverPreview(category.cover_image || null);
    } else {
      setEditingCategory(null);
      setFormData({ name: '', description: '' });
      setCoverPreview(null);
    }
    setCoverImage(null);
    setShowModal(true);

    setTimeout(() => {
      const el = document.querySelector('input[name="name"]');
      if (el) el.focus();
    }, 60);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({ name: '', description: '' });
    setCoverImage(null);
    setCoverPreview(null);
  };

  const setCoverFromFile = (file) => {
    if (!file) return;
    setCoverImage(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const drop = useDropzone(setCoverFromFile);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setCoverFromFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      if (coverImage) formDataToSend.append('cover_image', coverImage);

      const url = editingCategory
        ? `${BACKEND_URL}/api/admin/mood-categories/${editingCategory.id}/update`
        : `${BACKEND_URL}/api/admin/mood-categories`;

      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formDataToSend,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Erreur serveur');
      }

      await loadCategories();
      closeModal();
    } catch (error) {
      alert('Erreur: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/mood-categories/${id}/delete`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Erreur serveur');
      }

      await loadCategories();
    } catch (error) {
      alert('Erreur: ' + error.message);
    }
  };

  const total = categories.length;
  const shown = filteredCategories.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-14">
        <Loader2 className="w-8 h-8 animate-spin text-white/70" />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Background vibe */}
      <div className="pointer-events-none absolute -top-24 -left-24 w-80 h-80 bg-fuchsia-500/20 blur-3xl" />
      <div className="pointer-events-none absolute -top-20 -right-24 w-80 h-80 bg-cyan-500/15 blur-3xl" />

      {/* Sticky header */}
      <div className="sticky top-0 z-20 -mx-2 px-2 pt-2 pb-4 backdrop-blur-xl bg-black/20">
        <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4 md:p-5 shadow-[0_20px_80px_-40px_rgba(0,0,0,0.8)]">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center gap-2 rounded-2xl bg-white/5 border border-white/10 px-3 py-1.5">
                  <Sparkles size={16} className="text-white/70" />
                  <span className="text-xs text-white/70">Mood Categories</span>
                </div>
                <div className="text-xs text-white/45">
                  {shown}/{total} affichée{shown > 1 ? 's' : ''}
                </div>
              </div>

 
 
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="relative w-full sm:w-96">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher une catégorie…"
                  className="w-full pl-10 pr-10 py-3 rounded-2xl bg-white/5 border border-white/10
                             focus:outline-none focus:border-white/25 focus:bg-white/10 transition"
                />
                {query?.trim() ? (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-xl hover:bg-white/10 transition"
                    aria-label="Effacer"
                    title="Effacer"
                  >
                    <X size={16} className="text-white/60" />
                  </button>
                ) : null}
              </div>

              <button
                onClick={() => openModal()}
                className="group inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl
                           bg-white text-black font-semibold hover:bg-white/90 transition active:scale-[0.99]"
              >
                <Plus size={18} />
                Nouveau
                <ChevronRight size={18} className="opacity-60 group-hover:opacity-90 transition" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Empty / No results / Grid */}
      {categories.length === 0 ? (
        <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.04] p-10 text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-4">
            <ImageIcon className="text-white/60" />
          </div>
          <p className="text-white/85 font-semibold text-lg">Aucune catégorie</p>
          <p className="text-white/55 mt-1">Crée ta première catégorie de mood.</p>
          <button
            onClick={() => openModal()}
            className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl
                       bg-white text-black font-semibold hover:bg-white/90 transition"
          >
            <Plus size={18} />
            Créer une catégorie
          </button>
        </div>
      ) : filteredCategories.length === 0 ? (
        <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.04] p-10 text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-4">
            <Search className="text-white/60" />
          </div>
          <p className="text-white/85 font-semibold text-lg">Aucun résultat</p>
          <p className="text-white/55 mt-1">Essaie un autre mot-clé.</p>
          <button
            onClick={() => setQuery('')}
            className="mt-6 px-5 py-3 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/10 transition"
          >
            Effacer la recherche
          </button>
        </div>
      ) : (
        <>
          {/* Business cards grid */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredCategories.map((category) => {
              const cover = category.cover_image;

              return (
                <div
                  key={category.id}
                  className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04]
                             hover:bg-white/[0.07] hover:border-white/15 transition
                             shadow-[0_18px_60px_-45px_rgba(0,0,0,0.9)]"
                >
                  {/* Cover top */}
                  <div className="relative h-20">
                    <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/35 via-indigo-500/25 to-cyan-500/25" />
                    {cover ? (
                      <img
                        src={cover}
                        alt={category.name}
                        className="absolute inset-0 w-full h-full object-cover opacity-85"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-white/60">
                        <ImageIcon size={18} />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                    {/* Actions (hover) */}
                    <div className="absolute top-2 right-2 flex gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition">
                      <button
                        type="button"
                        onClick={() => openModal(category)}
                        className="p-2 rounded-2xl bg-black/40 border border-white/15 hover:bg-black/55 transition"
                        title="Modifier"
                        aria-label="Modifier"
                      >
                        <Edit2 size={14} className="text-white/85" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(category.id)}
                        className="p-2 rounded-2xl bg-black/40 border border-white/15 hover:bg-black/55 transition"
                        title="Supprimer"
                        aria-label="Supprimer"
                      >
                        <Trash2 size={14} className="text-red-200" />
                      </button>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="px-4 pb-4 pt-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="text-base font-semibold truncate">{category.name}</h3>
                        <p className="mt-1 text-sm text-white/55 leading-snug line-clamp-2">
                          {category.description || 'Aucune description.'}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-[11px] text-white/70 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">
                        {category.mood_count || 0} mood(s)
                      </span>

                      {/* Mobile actions */}
                      <div className="flex gap-2 sm:hidden">
                        <button
                          type="button"
                          onClick={() => openModal(category)}
                          className="px-3 py-2 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/10 transition text-sm"
                        >
                          Modifier
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(category.id)}
                          className="px-3 py-2 rounded-2xl bg-red-500/10 hover:bg-red-500/15 border border-red-400/20 transition text-sm text-red-200"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* subtle shine */}
                  <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition">
                    <div className="absolute -top-16 right-6 w-40 h-40 bg-fuchsia-500/10 blur-3xl" />
                    <div className="absolute -bottom-16 left-6 w-40 h-40 bg-cyan-500/10 blur-3xl" />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 text-sm text-white/45">
            {shown} catégorie{shown > 1 ? 's' : ''} affichée{shown > 1 ? 's' : ''} (sur {total})
          </div>
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 modal-text"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !saving) closeModal();
          }}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

          <div className="relative mx-auto mt-6 mb-6 w-[min(980px,calc(100%-24px))]">
            <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[#07080b] shadow-[0_40px_120px_-60px_rgba(0,0,0,0.9)]">
              {/* Glows */}
              <div className="pointer-events-none absolute -top-24 -left-24 w-80 h-80 bg-fuchsia-500/20 blur-3xl" />
              <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 bg-cyan-500/15 blur-3xl" />

              {/* Header */}
              <div className="flex items-center justify-between gap-4 px-6 md:px-8 py-5 border-b border-white/10">
                <div className="min-w-0">
                  <h3 className="text-xl md:text-2xl font-semibold tracking-tight">
                    {editingCategory ? 'Modifier la catégorie' : 'Créer une catégorie'}
                  </h3>
                  <p className="text-white/55 text-sm mt-1">
                    Ajoute une cover + un nom clair (ex: Chill, Focus, Sleep).
                  </p>
                </div>

                <button
                  onClick={closeModal}
                  disabled={saving}
                  className="p-2.5 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition disabled:opacity-50"
                  aria-label="Fermer"
                >
                  <X size={18} className="text-white/80" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-[1fr_420px] gap-0">
                {/* LEFT: Preview/Dropzone */}
                <div className="relative border-b md:border-b-0 md:border-r border-white/10 p-6 md:p-8">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />

                  <div
                    className={cn(
                      'relative h-48 md:h-56 rounded-3xl overflow-hidden border border-white/10 bg-white/5 cursor-pointer',
                      drop.isOver ? 'ring-2 ring-white/40' : 'ring-0'
                    )}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={drop.onDragOver}
                    onDragLeave={drop.onDragLeave}
                    onDrop={drop.onDrop}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/25 via-indigo-500/15 to-cyan-500/15" />
                    {coverPreview ? (
                      <img src={coverPreview} alt="Preview" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
                        <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center">
                          <Upload size={20} className="text-white/70" />
                        </div>
                        <div className="mt-3 text-sm font-semibold text-white/80">
                          Dépose une image ici
                        </div>
                        <div className="text-xs text-white/50 mt-1">
                          ou clique pour choisir un fichier
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
                    <div className="absolute bottom-3 left-3 text-xs text-white/80 bg-black/40 border border-white/15 px-3 py-1.5 rounded-full">
                      Cover
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={saving}
                      className="px-3 py-2 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/10 transition text-sm disabled:opacity-50"
                    >
                      Remplacer
                    </button>

                    {(coverPreview || coverImage) && (
                      <button
                        type="button"
                        onClick={() => {
                          setCoverImage(null);
                          setCoverPreview(editingCategory?.cover_image || null);
                        }}
                        disabled={saving}
                        className="px-3 py-2 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition text-sm text-white/70 disabled:opacity-50"
                      >
                        Retirer
                      </button>
                    )}
                  </div>

                  {/* Mini preview card */}
                  <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.04] overflow-hidden">
                    <div className="relative h-16">
                      <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/35 via-indigo-500/25 to-cyan-500/25" />
                      {coverPreview ? (
                        <img src={coverPreview} alt="mini cover" className="absolute inset-0 w-full h-full object-cover opacity-85" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-white/60">
                          <ImageIcon size={18} />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    </div>
                    <div className="p-4">
                      <div className="text-xs text-white/50">Aperçu</div>
                      <div className="mt-1 text-sm font-semibold text-white/85 truncate">
                        {formData.name || 'Nom de la catégorie'}
                      </div>
                      <div className="text-xs text-white/50 mt-1 line-clamp-2">
                        {formData.description || 'Description…'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT: Form */}
                <div className="p-6 md:p-8">
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">Nom *</label>
                      <input
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        disabled={saving}
                        className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10
                                   focus:outline-none focus:border-white/25 focus:bg-white/10 transition disabled:opacity-50"
                        placeholder="Ex: Relaxation, Focus, Sommeil…"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        disabled={saving}
                        className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10
                                   focus:outline-none focus:border-white/25 focus:bg-white/10 transition disabled:opacity-50 resize-none"
                        rows={6}
                        placeholder="Décris la catégorie…"
                      />
                      <div className="mt-2 text-xs text-white/45">2 lignes max = rendu plus clean.</div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                      <button
                        type="button"
                        onClick={closeModal}
                        disabled={saving}
                        className="flex-1 px-4 py-3 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/10 transition disabled:opacity-50"
                      >
                        Annuler
                      </button>

                      <button
                        type="submit"
                        disabled={saving}
                        className="flex-1 px-4 py-3 rounded-2xl bg-white text-black font-semibold hover:bg-white/90 transition disabled:opacity-50"
                      >
                        {saving ? (
                          <span className="inline-flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Enregistrement…
                          </span>
                        ) : editingCategory ? (
                          'Mettre à jour'
                        ) : (
                          'Créer'
                        )}
                      </button>
                    </div>

                    <div className="text-xs text-white/45">
                      Raccourci : <span className="text-white/70">ESC</span> pour fermer (hors upload).
                    </div>
                  </div>
                </div>
              </form>

              {/* Optional progress bar (simple) */}
              {saving && (
                <div className="px-6 md:px-8 pb-6">
                  <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-white rounded-full transition-all"
                      style={{ width: `${clamp(60, 0, 100)}%` }}
                    />
                  </div>
                  <div className="mt-2 text-xs text-white/50">Sauvegarde en cours…</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
